import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Trash2, Search, Users, Network, Folder } from 'lucide-react'
import { useRelationshipStore } from '../../../../stores/relationshipStore'
import { useFolderStore } from '../../../../stores/folderStore'
import type { SavedRelationship } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedRelationshipsListProps {
  relationships: SavedRelationship[]
  onLoad: (relationship: SavedRelationship) => void
}

export function SavedRelationshipsList({ relationships, onLoad }: SavedRelationshipsListProps) {
  const { deleteRelationship, exportAsText, exportAsJSON, importFromJSON } = useRelationshipStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredRelationships = relationships
  if (selectedFolderId === 'all') {
    folderFilteredRelationships = relationships
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('relationship')
    folders.forEach(folder => {
      getItemsInFolder('relationship', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredRelationships = relationships.filter(rel => !allFolderItemIds.has(rel.id))
  } else {
    const itemIds = getItemsInFolder('relationship', selectedFolderId)
    folderFilteredRelationships = relationships.filter(rel => itemIds.includes(rel.id))
  }

  const filteredRelationships = folderFilteredRelationships.filter(rel =>
    rel.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rel.result.characters.some(char => 
      char.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  )

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('この人間関係を削除しますか？')) {
      deleteRelationship(id)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="タイトルやキャラクター名で検索"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFolders(!showFolders)}
          className="px-2"
        >
          <Folder className="h-4 w-4" />
        </Button>
        <ExportImportButtons
          onExportText={exportAsText}
          onExportJSON={exportAsJSON}
          onImportJSON={importFromJSON}
          dataTypeName="関係性"
        />
      </div>

      <div className="flex gap-4">
        {/* フォルダサイドバー */}
        {showFolders && (
          <div className="w-48 border-r pr-4">
            <FolderManager
              generatorType="relationship"
              items={relationships}
              selectedFolderId={selectedFolderId}
              onFolderSelect={setSelectedFolderId}
              draggedItemId={draggedItemId}
              onItemMove={(itemId, toFolderId) => {
                const currentFolder = getItemFolder('relationship', itemId)
                moveItemBetweenFolders('relationship', itemId, currentFolder?.id || null, toFolderId)
              }}
            />
          </div>
        )}
        
        {/* 人間関係リスト */}
        <div className="flex-1">
          <ScrollArea className="h-[500px] w-full">
            {filteredRelationships.length === 0 ? (
              <div className="card p-8 text-center">
                <Network className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {searchTerm ? '検索結果がありません' : '保存された人間関係がありません'}
                </p>
              </div>
            ) : (
              <div className="grid gap-3">
                {filteredRelationships.map((rel) => (
                  <div
                    key={rel.id}
                    className="card p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => onLoad(rel)}
                    draggable
                    onDragStart={(e) => {
                      setDraggedItemId(rel.id)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragEnd={() => {
                      setDraggedItemId(null)
                    }}
                    style={{
                      opacity: draggedItemId === rel.id ? 0.5 : 1
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{rel.title}</h3>
                          <Badge variant="secondary">
                            <Users className="h-3 w-3 mr-1" />
                            {rel.result.characters.length}人
                          </Badge>
                          <Badge variant="outline">
                            関係性: {rel.result.relationships.length}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {rel.result.summary}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>作成: {new Date(rel.createdAt).toLocaleDateString()}</span>
                          {rel.tags && rel.tags.length > 0 && (
                            <div className="flex gap-1">
                              {rel.tags.map((tag, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* フォルダ選択 */}
                        <div className="mt-2 w-48">
                          <FolderMoveSelect
                            generatorType="relationship"
                            itemId={rel.id}
                            currentFolderId={getItemFolder('relationship', rel.id)?.id}
                            onMove={() => {}}
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDelete(rel.id, e)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        {relationships.length}/200件を使用中
      </p>
    </div>
  )
}